// Shared presentational pieces. Everything is dark-theme-only for v1 and reads
// from the NetComTV tokens in index.css.
import clsx from "clsx";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

// --- time ------------------------------------------------------------------
// doc 03 §5: display times are America/Chicago; storage is UTC.
const TZ = "America/Chicago";

export function formatTime(iso: string | null | undefined, withDate = true): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    timeZone: TZ,
    ...(withDate ? { year: "numeric", month: "short", day: "2-digit" } : {}),
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { timeZone: TZ, year: "numeric", month: "short", day: "2-digit" });
}

export function formatAgo(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "never";
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds) return "—";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function formatMoney(value: string | number | null | undefined, currency = "USD"): string {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (n === null || n === undefined || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency });
}

// --- status ----------------------------------------------------------------
export type Tone = "ok" | "warn" | "crit" | "info" | "idle" | "brand";

const toneClasses: Record<Tone, string> = {
  ok: "bg-state-ok/15 text-state-ok",
  warn: "bg-state-warn/15 text-state-warn",
  crit: "bg-state-crit/15 text-state-crit",
  info: "bg-state-info/15 text-state-info",
  idle: "bg-ink-600 text-ink-300",
  brand: "bg-brand/15 text-brand-on",
};

const dotClasses: Record<Tone, string> = {
  ok: "bg-state-ok",
  warn: "bg-state-warn",
  crit: "bg-state-crit",
  info: "bg-state-info",
  idle: "bg-state-idle",
  brand: "bg-brand",
};

export function Badge({ tone = "idle", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={clsx("badge", toneClasses[tone], className)}>{children}</span>;
}

/** A status dot always ships with its label — never colour alone. */
export function StatusDot({ tone, label, pulse }: { tone: Tone; label: string; pulse?: boolean }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={clsx("h-2 w-2 shrink-0 rounded-full", dotClasses[tone], pulse && "animate-pulse-dot")}
        aria-hidden
      />
      <span>{label}</span>
    </span>
  );
}

export function endpointTone(state: string, online?: boolean): Tone {
  if (state === "active") return online === false ? "crit" : "ok";
  if (state === "provisioned") return "info";
  if (state === "inventory") return "idle";
  if (state === "suspended") return "warn";
  if (state === "revoked") return "crit";
  return "idle";
}

export function severityTone(severity: string): Tone {
  return severity === "P1" ? "crit" : severity === "P2" ? "warn" : "info";
}

// --- layout ----------------------------------------------------------------
export function Card({
  title,
  actions,
  children,
  className,
  padded = true,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <section className={clsx("card", className)}>
      {(title || actions) && (
        <header className="card-header">
          <h2 className="card-title">{title}</h2>
          {actions}
        </header>
      )}
      <div className={padded ? "p-4" : undefined}>{children}</div>
    </section>
  );
}

/** The eyebrow above the title is the NetComTV pack's signature device: a short
 *  uppercase amber label that says what kind of thing you are looking at. */
export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  eyebrow?: string;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4 border-b border-ink-600 pb-4">
      <div>
        {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
        <h1 className="text-2xl tracking-tight text-ink-100">{title}</h1>
        {subtitle && <p className="mt-1 max-w-3xl text-sm text-ink-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}) {
  // The pack treats a figure as a headline: 800 weight, tight, tabular, closed
  // by an amber rule at the foot of the band.
  return (
    <div className="stat-band p-4">
      <div className="eyebrow-muted">{label}</div>
      <div className={clsx("figure mt-1.5 text-stat", tone ? toneClasses[tone].split(" ")[1] : "text-ink-100")}>
        {value}
      </div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  );
}

/** A labelled reading inside a card — smaller than a Stat, for dense grids. */
export function Metric({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-md border border-ink-600 bg-ink-800/60 p-3">
      <div className="eyebrow-muted">{label}</div>
      <div className="figure mt-1 text-lg text-ink-100">{value}</div>
    </div>
  );
}

/** A labelled meter. The number is always present; the bar is the shortcut —
 *  never colour alone, per the pack's status rules. */
export function Meter({
  label,
  pct,
  tone = "ok",
  detail,
}: {
  label: string;
  pct: number;
  tone?: Tone;
  detail?: ReactNode;
}) {
  const width = Math.max(0, Math.min(100, pct));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="eyebrow-muted">{label}</span>
        <span className="font-mono text-sm text-ink-100">{pct.toFixed(0)}%</span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-ink-600">
        <div className={clsx("h-full rounded-full", dotClasses[tone])} style={{ width: `${width}%` }} />
      </div>
      {detail && <div className="mt-1 text-xs text-ink-400">{detail}</div>}
    </div>
  );
}

/** One horizontal bar split by state — the honest alternative to a donut for
 *  "how is the fleet distributed". Each segment carries its own label+count in
 *  the legend, so identity is never colour alone. */
export function SegmentBar({
  segments,
  ariaLabel,
}: {
  segments: { label: string; count: number; tone: Tone }[];
  ariaLabel: string;
}) {
  const total = segments.reduce((sum, s) => sum + s.count, 0);
  const visible = segments.filter((s) => s.count > 0);
  if (total === 0) return <Empty>Nothing to show yet.</Empty>;
  return (
    <div>
      <div
        className="flex h-2.5 w-full gap-px overflow-hidden rounded-full"
        role="img"
        aria-label={ariaLabel}
      >
        {visible.map((s) => (
          <div
            key={s.label}
            className={clsx("h-full", dotClasses[s.tone])}
            style={{ width: `${(s.count / total) * 100}%` }}
            title={`${s.label}: ${s.count}`}
          />
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-ink-300">
        {visible.map((s) => (
          <span key={s.label} className="inline-flex items-center gap-1.5">
            <span className={clsx("h-2 w-2 rounded-sm", dotClasses[s.tone])} aria-hidden />
            {s.label}
            <span className="font-mono text-ink-100">{s.count}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-ink-400">{children}</div>;
}

export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 px-4 py-10 text-sm text-ink-400">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-ink-500 border-t-brand" />
      {label}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="rounded-md border border-state-crit/40 bg-state-crit/10 px-4 py-3 text-sm text-state-crit">
      {message}
    </div>
  );
}

// --- forms -----------------------------------------------------------------
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-state-crit">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-ink-400">{hint}</span>
      ) : null}
    </label>
  );
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-6 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={clsx("card my-8 w-full shadow-pop", wide ? "max-w-4xl" : "max-w-lg")}>
        <header className="card-header">
          <h2 className="card-title">{title}</h2>
          <button className="btn-ghost px-2 py-1" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="p-4">{children}</div>
        {footer && <footer className="flex justify-end gap-2 border-t border-ink-600 px-4 py-3">{footer}</footer>}
      </div>
    </div>
  );
}

export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  className,
  danger,
}: {
  label: string;
  confirmLabel?: string;
  onConfirm: () => unknown;
  className?: string;
  danger?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    // Disarm on its own so a half-pressed destructive action does not sit
    // waiting for a stray click five minutes later.
    const t = setTimeout(() => setArmed(false), 5000);
    return () => clearTimeout(t);
  }, [armed]);

  return (
    <button
      className={clsx(danger ? "btn-danger" : "btn-secondary", className)}
      onClick={() => {
        if (armed) {
          setArmed(false);
          void onConfirm();
        } else {
          setArmed(true);
        }
      }}
    >
      {armed ? (confirmLabel ?? `${label} — click again`) : label}
    </button>
  );
}

// --- toasts ----------------------------------------------------------------
type Toast = { id: number; message: string; tone: Tone };
const ToastContext = createContext<(message: string, tone?: Tone) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((message: string, tone: Tone = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, message, tone }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), 6000);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-96 flex-col gap-2">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={clsx(
              "pointer-events-auto rounded-md border px-4 py-3 text-sm shadow-pop",
              toast.tone === "crit"
                ? "border-state-crit/40 bg-ink-800 text-state-crit"
                : toast.tone === "warn"
                  ? "border-state-warn/40 bg-ink-800 text-state-warn"
                  : "border-ink-500 bg-ink-800 text-ink-100",
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Wraps an async action so failures surface as a toast rather than silence. */
export function useAction() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async (fn: () => Promise<unknown>, success?: string) => {
      setBusy(true);
      try {
        await fn();
        if (success) toast(success, "ok");
        return true;
      } catch (error) {
        toast(error instanceof Error ? error.message : String(error), "crit");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [toast],
  );
  return { run, busy };
}
